import { X } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';

import { Button } from './Button';

type DialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description?: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    /**
     * Tailwind max-width class for the dialog panel. Defaults to 'max-w-2xl'.
     */
    maxWidth?: string;
    /**
     * Optional slot rendered below the header title/description and
     * outside the scrollable body — typically a tab list. Sits flush
     * against the header's divider so the tabs read as part of the
     * dialog chrome.
     */
    headerSlot?: ReactNode;
    /**
     * When set, locks the dialog content area to this Tailwind height
     * class (e.g. `h-[80vh]` or `h-[600px]`) so the dialog doesn't
     * resize as tab content swaps. The body remains scrollable.
     * Defaults to fluid height capped at `max-h-[90vh]`.
     */
    fixedHeight?: string;
    /**
     * When false, the dialog can only be closed via its own controls (e.g. an
     * "OK" button in the footer) — backdrop click, Escape, and the header X are
     * all disabled. Use for messages that MUST be acknowledged. Defaults to true.
     */
    dismissible?: boolean;
    /**
     * When false, clicking the backdrop (outside the panel) does NOT close the
     * dialog — Escape and the header X still do. Use for forms where an
     * accidental outside click would discard the user's input. Defaults to true.
     */
    closeOnBackdrop?: boolean;
};

export function Dialog({
    open,
    onOpenChange,
    title,
    description,
    children,
    footer,
    maxWidth = 'max-w-2xl',
    headerSlot,
    fixedHeight,
    dismissible = true,
    closeOnBackdrop = true,
}: DialogProps) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);
    // Stash the latest onOpenChange in a ref so we can call it from the
    // Escape handler without making the effect re-run whenever a parent
    // recreates the callback. Without this, every keystroke that updates
    // dialog-local state caused the parent to re-render with a fresh
    // `(next) => ...` wrapper, retriggering the effect, refocusing the
    // dialog root, and stealing focus away from whatever input the user
    // was typing into.
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    useEffect(() => {
        if (!open) return;

        previouslyFocused.current = document.activeElement as HTMLElement | null;
        const node = dialogRef.current;
        if (node) node.focus();

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && dismissible) {
                e.preventDefault();
                onOpenChangeRef.current(false);
            }
        };
        document.addEventListener('keydown', onKey);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
            previouslyFocused.current?.focus?.();
        };
    }, [open, dismissible]);

    const onBackdrop = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (dismissible && closeOnBackdrop && e.target === e.currentTarget) onOpenChange(false);
        },
        [onOpenChange, dismissible, closeOnBackdrop],
    );

    if (!open || typeof document === 'undefined') return null;

    // Portal INTO `.v2-root` (not document.body) so the scoped v2 preflight
    // resets (strip native button chrome, list/heading defaults) and the
    // dark-mode class on the ancestor wrapper both reach the dialog content.
    // Matches `Modal`. Falls back to body if the root isn't mounted yet — the
    // backdrop carries `v2-reset` so the resets still apply when the dialog is
    // hosted on a legacy (v1) page with no v2 shell around. NOT `v2-root`:
    // several portal lookups (this one included) querySelector '.v2-root' as
    // "the app shell", and a backdrop matching that makes the dialog try to
    // portal into itself — an infinite mount/unmount flicker.
    const target = document.querySelector('.v2-root') ?? document.body;

    return createPortal(
        <div
            className="v2-reset fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
            onMouseDown={onBackdrop}
            role="presentation"
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="dialog-title"
                tabIndex={-1}
                className={cn(
                    'flex w-full flex-col rounded-t-lg bg-white shadow-xl outline-none dark:bg-gray-800 sm:rounded-lg',
                    fixedHeight ? cn('max-h-[90vh]', fixedHeight) : 'max-h-[90vh]',
                    maxWidth,
                )}
            >
                <header
                    className={cn(
                        'flex items-start justify-between gap-4 px-4 pt-3 dark:border-gray-700 sm:px-6 sm:pt-4',
                        // Border lives on the headerSlot when present
                        // (so the tabs sit flush against it), otherwise
                        // on the header itself.
                        headerSlot ? 'pb-3 sm:pb-4' : 'border-b border-solid border-gray-200 pb-3 sm:pb-4',
                    )}
                >
                    <div className="min-w-0 flex-1">
                        <h2
                            id="dialog-title"
                            className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg"
                        >
                            {title}
                        </h2>
                        {description && (
                            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</div>
                        )}
                    </div>
                    {dismissible && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onOpenChange(false)}
                            aria-label="Inchide"
                            className="-mr-2 shrink-0"
                        >
                            <X aria-hidden="true" className="h-4 w-4" />
                        </Button>
                    )}
                </header>
                {headerSlot && (
                    <div className="border-b border-solid border-gray-200 px-4 pt-2 dark:border-gray-700 sm:px-6 sm:pt-3">
                        {headerSlot}
                    </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">{children}</div>
                {footer && (
                    <footer className="flex flex-col-reverse items-stretch gap-2 border-t border-solid border-gray-200 px-4 py-3 dark:border-gray-700 sm:flex-row sm:justify-end sm:px-6 sm:py-4">
                        {footer}
                    </footer>
                )}
            </div>
        </div>,
        target,
    );
}
